import { AngBridgeUtil } from "../../util/ang.bridge.util";
import type { ReservationAttemptDetails } from "../../modules/s25-chat-bot/s25.express.scheduling.chat.bot";
import type { CabinetSelectModal } from "../../modules/modal/modals/modal.cabinet.select.component";
import { ModalService } from "../../modules/modal/modal.service";
import { AlertService } from "../../services/alert.service";
import { BpeService } from "../../modules/bpe/bpe.service";
import { Item } from "../../pojo/Item";
import { AccessLevels } from "../../pojo/Fls";
import { DatetimeService } from "../../services/datetime.service";
import { EventEmailService } from "../../services/event.email.service";
import { EventService } from "../../services/event.service";
import { FlsService } from "../../services/fls.service";
import { NotificationService } from "../../services/notification.service";
import { PreferenceService } from "../../services/preference.service";
import { SpaceService } from "../../services/space.service";
import { StateService } from "../../services/state.service";
import { UserprefService } from "../../services/userpref.service";
import { S25Util } from "../../util/s25-util";
import { S25Assert } from "../../util/s25.assert";
import {
    EXPRESS_FORM_SETUP_DELAY_MS,
    EXPRESS_MAX_CREATE_YEARS_FUTURE,
    EXPRESS_MIN_CREATE_YEARS_PAST,
    EXPRESS_PROFILE_CODE,
    EXPRESS_PROMISE_YIELD_MS,
    EXPRESS_SAVE_EVENT_STATE,
    type ExpressSchedulingSaveOptions,
    type ExpressSchedulingSelectedParentId,
    toRoseExpressParentId,
    type EventFormModelUtilFactory,
    type ExpressEventSaveMessage,
    type ExpressSchedulingDateModel,
    type ExpressSchedulingHttpError,
    type ExpressSchedulingInitContext,
    type ExpressSchedulingLocationDropdownApi,
    type ExpressSchedulingLocationFilter,
    type ExpressSchedulingLocationItem,
    type ExpressSchedulingPanelApi,
    type ExpressSchedulingQuotaConflict,
    type ExpressSchedulingSaveLocation,
    type ExpressSchedulingTimeRange,
    type ExpressUserGroupRoseSecurity,
} from "./s25.express.scheduling.types";

const DURATION_WARNING =
    "The selected location only allows events up to {{LOCATION_MINUTES}} minutes long. Your current event duration is {{EVENT_MINUTES}} minutes.";

function delay(milliseconds: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

function noop(): void {}

/**
 * Imperative express-scheduling workflow; this class owns async/event-form coordination.
 * UI template state lives on {@link S25ExpressSchedulingComponent}.
 */
export class S25ExpressSchedulingHost {
    dateModel: ExpressSchedulingDateModel | undefined;
    readonly locationDropdownApi: ExpressSchedulingLocationDropdownApi = {};
    readonly locationFilter: ExpressSchedulingLocationFilter;

    hasLocationDropdown = true;
    hasAiFeaturesEnabled = false;
    hasEventForm = false;
    hasExpressPerms = false;
    init = false;
    postSavePage = false;
    canCreateExpress = false;
    durationWarning = "";
    quotaWarning = "";
    pastDateWarning = "";
    pastDateOverride = false;
    name = "";
    location: ExpressSchedulingLocationItem | null = null;
    notifications: string[] = [];

    private readonly availabilityPromises: Promise<unknown>[] = [];
    private readonly locationPromises: Promise<unknown>[] = [];
    private readonly quotaPromises: Promise<unknown>[] = [];
    private currLocationId: number | null = null;
    private touchedDateTimes = false;
    private userTouchedDateTimePicker = false;
    private userSelectedLocation = false;
    private destroyed = false;

    constructor(
        private readonly initContext: ExpressSchedulingInitContext | undefined,
        private readonly panelApi: ExpressSchedulingPanelApi | undefined,
        private readonly onStateChange: () => void,
    ) {
        this.locationFilter = {
            startDt: S25Util.date.toStartOfHour(new Date()),
            endDt: S25Util.date.toStartOfHour(S25Util.date.addHours(new Date(), 1)),
            getAvailableItems: (items) => this.filterAvailableExpressLocations(items),
        };
    }

    markDestroyed(): void {
        this.destroyed = true;
    }

    async bootstrap(): Promise<boolean> {
        const [hasExpressPerms, fls, aiPreferences] = await Promise.all([
            EventService.hasExpressPerms(),
            FlsService.getFls(),
            PreferenceService.getPreferences(["HasAiFeatures"], "U"),
        ]);
        if (!this.isAlive()) {
            return false;
        }

        this.hasExpressPerms = hasExpressPerms;
        this.hasEventForm = fls?.SPEEDBOOK === AccessLevels.Full;
        this.hasAiFeaturesEnabled = aiPreferences.HasAiFeatures?.value === "T";

        const panelApi = this.panelApi;
        if (panelApi) {
            panelApi.hasExpressPerms = this.hasExpressPerms;
            panelApi.hasNoExpressPerms = !this.hasExpressPerms;
            panelApi.init = true;
            if (this.hasExpressPerms) {
                panelApi.getModel = () => {
                    const timeRange = this.getTimeRange();
                    S25Assert.isDefined(timeRange, "Express draft model requires a date/time range");
                    return {
                        name: this.name,
                        startDt: timeRange.startDt,
                        endDt: timeRange.endDt,
                        location: this.location,
                    };
                };
                panelApi.reset = () => void this.reset();
            }
        }

        if (!this.hasExpressPerms) {
            this.notifyStateChange();
            return false;
        }

        await this.initializeExpressForm();
        return this.isAlive();
    }

    async reset(): Promise<void> {
        this.init = false;
        this.canCreateExpress = false;
        this.availabilityPromises.length = 0;
        this.locationPromises.length = 0;
        this.quotaPromises.length = 0;
        this.name = "";
        this.location = null;
        this.touchedDateTimes = false;
        this.userTouchedDateTimePicker = false;
        this.userSelectedLocation = false;
        this.quotaWarning = "";
        this.pastDateWarning = "";
        this.pastDateOverride = false;
        this.postSavePage = false;
        this.notifications = [];
        await this.initializeExpressForm();
    }

    exitPostSavePage(): void {
        this.postSavePage = false;
        this.notifyStateChange();
    }

    applyDateTimeRange(startDt: Date, endDt: Date): void {
        if (!this.dateModel) {
            return;
        }
        this.dateModel.timeModel.evStartDt = startDt;
        this.dateModel.timeModel.evEndDt = endDt;
        this.pastDateOverride = false;
        this.syncOccurrenceTimesFromTimeModel();
    }

    syncOccurrenceTimesFromTimeModel(): void {
        if (!this.dateModel?.timeModel.evStartDt || !this.dateModel.timeModel.evEndDt) {
            return;
        }
        const startDt = this.dateModel.timeModel.evStartDt;
        const endDt = this.dateModel.timeModel.evEndDt;
        for (const occurrence of this.dateModel.occurrences) {
            if (!occurrence.evStartDt || !occurrence.evEndDt) {
                continue;
            }
            S25Util.date.syncDateAll(occurrence.evStartDt, startDt);
            S25Util.date.syncDateAll(occurrence.evEndDt, endDt);
        }
    }

    onDateTimeChange = (): Promise<unknown> => {
        const dateModel = this.dateModel;
        const timeRange = this.getTimeRange();
        if (!dateModel || !timeRange) {
            return Promise.resolve();
        }
        this.syncOccurrenceTimesFromTimeModel();
        this.locationFilter.startDt = timeRange.startDt;
        this.locationFilter.endDt = timeRange.endDt;
        this.touchedDateTimes = true;
        this.userTouchedDateTimePicker = true;
        this.updatePastDateWarning();
        this.canCreateExpress = false;
        this.hasLocationDropdown = false;
        this.resetLocationDropdownApi();
        this.currLocationId = this.location?.itemId ?? null;
        this.notifyStateChange();

        return Promise.all(this.availabilityPromises).then(() => {
            if (!this.isAlive()) {
                return;
            }
            if (this.location) {
                this.quotaPromises.push(dateModel.checkQuotas(dateModel, this.location));
            } else {
                this.clearOccurrenceQuotaState(dateModel);
                this.quotaWarning = "";
            }
            this.location = null;
            return delay(0).then(() => {
                if (!this.isAlive()) {
                    return;
                }
                this.hasLocationDropdown = true;
                void this.setQuotaWarning();
                this.notifyStateChange();
            });
        });
    };

    onLocationsLoad = (): Promise<unknown> => {
        if (this.currLocationId) {
            return delay(0).then(() => {
                if (!this.isAlive()) {
                    return;
                }
                const items = this.locationDropdownApi.getItems?.() ?? [];
                const item = S25Util.array.getByProp(items, "itemId", this.currLocationId);
                this.location = item ?? null;
                this.currLocationId = null;
                if (this.location) {
                    return this.onSelectLocation().then(() => this.setDurationWarning());
                }
                return this.setDurationWarning();
            });
        }
        return this.setDurationWarning();
    };

    onSelectLocation = (fromUserSelection = false): Promise<unknown> => {
        if (!this.dateModel || !this.location) {
            return Promise.resolve();
        }
        if (fromUserSelection) {
            this.userSelectedLocation = true;
            this.updatePastDateWarning();
        }
        this.normalizeLocationItemType(this.location);
        this.syncExpressEventTypeOnModel(this.location);
        this.canCreateExpress = false;
        this.notifyStateChange();
        this.locationPromises.push(this.dateModel.setObjectDetails([this.location]));
        this.quotaPromises.push(this.dateModel.checkQuotas(this.dateModel, this.location));
        return this.promiseWait().then(
            () => {
                if (!this.isAlive()) {
                    return;
                }
                this.updatePastDateWarning();
                void this.setDurationWarning();
                void this.setQuotaWarning();
            },
            () => {
                if (!this.isAlive()) {
                    return;
                }
                void this.setDurationWarning();
            },
        );
    };

    aiSave = async (reservationAttemptDetails: ReservationAttemptDetails): Promise<unknown> => {
        if (!this.dateModel || !this.isAlive()) {
            return;
        }
        this.name = reservationAttemptDetails.selectedDetails.eventName;
        this.dateModel.timeModel.evStartDt = S25Util.date.parse(reservationAttemptDetails.selectedDetails.startDate);
        this.dateModel.timeModel.evEndDt = S25Util.date.parse(reservationAttemptDetails.selectedDetails.endDate);
        this.locationFilter.startDt = this.dateModel.timeModel.evStartDt;
        this.locationFilter.endDt = this.dateModel.timeModel.evEndDt;
        this.location = reservationAttemptDetails.selectedSpace as unknown as ExpressSchedulingLocationItem;
        await this.onSelectLocation();
        if (!this.isAlive()) {
            return;
        }
        return this.createExpressEvent({ datesAlreadyChecked: true });
    };

    openInForm(): void {
        if (!this.dateModel) {
            return;
        }
        const params: Record<string, unknown> = {
            eventName: this.name,
            itemId: this.location?.itemId,
            express: true,
        };
        if (this.touchedDateTimes) {
            params["startDt"] = this.dateModel.timeModel.evStartDt;
            params["startTime"] = this.dateModel.timeModel.evStartDt;
            params["endDt"] = this.dateModel.timeModel.evEndDt;
            params["endTime"] = this.dateModel.timeModel.evEndDt;
        }
        void StateService.createEvent(params, Item.Ids.Location);
        if (this.initContext?.closeModal) {
            this.initContext.closeModal();
        } else {
            void this.reset();
        }
    }

    createExpressEvent(saveOptions: ExpressSchedulingSaveOptions = {}): Promise<unknown> {
        return this.promiseWait().then(async () => {
            await this.ensureSelectedLocationDetails();
            return this.runCreateExpressEvent(saveOptions);
        });
    }

    private async runCreateExpressEvent(saveOptions: ExpressSchedulingSaveOptions): Promise<unknown> {
        const { datesAlreadyChecked = false } = saveOptions;
        if (!this.dateModel || !this.isAlive()) {
            return;
        }

        const startsInFuture = datesAlreadyChecked || this.validateStartsInFuture();

        if (!this.name) {
            this.showValidationError("Please enter a valid event name");
            return;
        }
        if (S25Util.isPrivate(this.name.toLowerCase().trim())) {
            this.showValidationError("Express events cannot be named '(Private)'");
            return;
        }
        if (!this.location) {
            this.showValidationError("Please select an express location");
            return;
        }
        if (!this.location.eventTypeId) {
            this.showValidationError("Express event type id not found. Please re-select or try another location");
            return;
        }
        const saveLocation = this.getSaveLocation(this.location);
        if (!saveLocation) {
            this.showValidationError("Please select an express location");
            return;
        }
        if (!startsInFuture) {
            this.notifyStateChange();
            return;
        }

        this.canCreateExpress = false;
        this.notifyStateChange();

        this.clearDateModelLocations();
        this.locationPromises.push(this.dateModel.addObject(saveLocation, true));
        this.quotaPromises.push(this.dateModel.checkQuotas(this.dateModel, saveLocation));

        await this.promiseWait();
        if (!this.isAlive()) {
            return;
        }

        await this.dateModel.checkAvailability(this.dateModel, this.dateModel.locations);
        if (!this.isAlive()) {
            return;
        }

        const unavailItems = this.dateModel.locations.filter((item) => item.checkedObj?.hasConflicts);
        const quotaConflicts = this.dateModel.occurrences.filter((item) => item.hasQuotaConflict);

        this.clearDateModelLocations();

        if (unavailItems.length) {
            void AlertService.alert(
                "Your chosen time and location combination is no longer available. Please adjust your choices and Save your event again.",
            );
            await this.reset();
            return;
        }

        if (quotaConflicts.length) {
            const conflict = quotaConflicts[0]?.quotaConflict;
            if (conflict) {
                void AlertService.alert(this.dateModel.getQuotaMsg(this.enrichQuotaConflict(conflict)));
            }
            await this.reset();
            return;
        }

        const eventIdPromise = S25Util.createPromise<number>();
        return BpeService.runScenarios({
            eventId: eventIdPromise.promise,
            change: () => this.saveExpressEvent(saveOptions, eventIdPromise),
            getSources: () => ["express"],
        });
    }

    private async saveExpressEvent(
        saveOptions: ExpressSchedulingSaveOptions,
        eventIdPromise: { resolve: (value: number) => void; reject: (reason?: unknown) => void },
    ): Promise<void> {
        const { selectedParentId, datesAlreadyChecked = false } = saveOptions;
        const dateModel = this.dateModel;
        const timeRange = this.getTimeRange();
        const saveLocation = this.location ? this.getSaveLocation(this.location) : undefined;
        if (!dateModel || !timeRange || !saveLocation || !this.isAlive()) {
            return;
        }

        try {
            const start = S25Util.date.toS25ISODateTimeStr(timeRange.startDt);
            const end = S25Util.date.toS25ISODateTimeStr(timeRange.endDt);
            if (!start || !end) {
                return;
            }

            const [sec, eventData] = await Promise.all([
                UserprefService.getUserGroupRoseSecurity(),
                this.postExpressEvent(saveLocation, start, end, selectedParentId),
            ]);
            if (!this.isAlive()) {
                return;
            }

            const eventIdRaw = S25Util.propertyGetVal(eventData, "event_id");
            eventIdPromise.resolve(parseInt(String(eventIdRaw), 10));

            const messages = S25Util.array.forceArray(S25Util.propertyGet(eventData, "messages"));
            if (messages?.length) {
                const errs = messages
                    .map((message) => message && S25Util.toStr((message as ExpressEventSaveMessage).msg_text))
                    .filter((message) => !!message)
                    .join("; ");
                if (errs) {
                    void AlertService.alert(errs);
                }
            }

            const eventId = parseInt(String(eventIdRaw), 10);

            if (!this.initContext) {
                await this.reset();
                if (!this.isAlive()) {
                    return;
                }
            }

            await StateService.putRecentlyViewedItemFetchName(Item.Ids.Event, eventId);
            EventEmailService.sendEventChangedEmail(eventId);
            await StateService.gotoItem({
                itemTypeId: Item.Ids.Event,
                itemId: eventId,
                forceView: "details",
            });
            if (!this.isAlive()) {
                return;
            }

            const roseSecurity = sec as ExpressUserGroupRoseSecurity;
            this.notifications = EventService.getEventSaveNotifications(
                eventData,
                roseSecurity.creation_success_msg ?? "",
            );
            for (const notification of this.notifications) {
                NotificationService.post(notification);
            }

            if (S25Util.isInIframe) {
                this.postSavePage = true;
            }

            this.initContext?.closeModal?.();
            this.notifyStateChange();
        } catch (error) {
            eventIdPromise.reject(error);
            if (!this.isAlive()) {
                return;
            }
            this.refreshCanCreateExpress();
            this.notifyStateChange();

            const httpError = error as ExpressSchedulingHttpError;
            const jsonErr = httpError.data ? S25Util.prettifyJson(httpError.data, null, { parent: true }) : undefined;
            const isMultiParent = S25Util.valueFind(jsonErr, "msg_id", "EV_I_MULTPAR");
            const parents = S25Util.propertyGet(jsonErr, "parent");
            if (isMultiParent && parents) {
                const modalData: CabinetSelectModal = { parent: parents };
                if (!this.isAlive()) {
                    return;
                }
                await ModalService.modal("cabinet-select", modalData);
                if (!this.isAlive()) {
                    return;
                }
                if (modalData.parentId) {
                    await this.createExpressEvent({
                        selectedParentId: modalData.parentId,
                        datesAlreadyChecked,
                    });
                }
            } else {
                const message = S25Util.errorText(error);
                if (message) {
                    void AlertService.alert(message);
                }
            }
        }
    }

    updatePastDateWarning(): void {
        const startDt = this.dateModel?.timeModel.evStartDt;
        if (!startDt) {
            this.pastDateWarning = "";
            if (this.init) {
                this.refreshCanCreateExpress();
            }
            return;
        }
        this.pastDateWarning =
            this.shouldShowPastDateWarning() && !this.pastDateOverride && startDt.getTime() < Date.now()
                ? "Warning: You are creating an event that begins in the past."
                : "";
        if (this.init) {
            this.refreshCanCreateExpress();
        }
    }

    ignorePastDateWarning(): void {
        this.pastDateOverride = true;
        this.updatePastDateWarning();
        this.refreshCanCreateExpress();
        this.notifyStateChange();
    }

    validateStartsInFuture(): boolean {
        this.updatePastDateWarning();
        return !this.pastDateWarning;
    }

    private shouldShowPastDateWarning(): boolean {
        return this.userTouchedDateTimePicker || this.userSelectedLocation;
    }

    private refreshCanCreateExpress(): void {
        const timeRange = this.getTimeRange();
        let durationAllowed = true;
        if (timeRange && this.location) {
            durationAllowed = this.validateDuration(timeRange.startDt, timeRange.endDt, this.location);
        }
        this.canCreateExpress = durationAllowed && !this.pastDateWarning;
    }

    private async initializeExpressForm(): Promise<void> {
        await this.buildDateModel();
        if (!this.isAlive()) {
            return;
        }
        //ANG-7578 only apply default dates if the availability grid or calendar didn't send some in
        const hasInitDateRange = !!(this.initContext?.startDt && this.initContext?.endDt);
        if (!hasInitDateRange) {
            await this.initializeDefaultDateTimeRange();
            if (!this.isAlive()) {
                return;
            }
        }
        await this.applyInitContext();
        if (!this.isAlive()) {
            return;
        }
        await delay(EXPRESS_FORM_SETUP_DELAY_MS);
        if (!this.isAlive()) {
            return;
        }
        await this.promiseWait();
        if (!this.isAlive()) {
            return;
        }
        this.canCreateExpress = true;
        this.refreshCanCreateExpress();
        this.init = true;
        this.notifyStateChange();
    }

    private async initializeDefaultDateTimeRange(): Promise<void> {
        if (!this.dateModel) {
            return;
        }
        if (this.dateModel.timeModel.evStartDt && this.dateModel.timeModel.evEndDt) {
            return;
        }
        const [currentHour, nextHour] = await Promise.all([DatetimeService.currentHour(), DatetimeService.nextHour()]);
        if (!this.isAlive()) {
            return;
        }
        this.dateModel.timeModel.evStartDt = currentHour;
        this.dateModel.timeModel.evEndDt = nextHour;
        this.dateModel.addOccurrence(
            currentHour,
            nextHour,
            this.dateModel.timeModel.minutes,
            false,
            EXPRESS_PROFILE_CODE,
        );
        this.locationFilter.startDt = currentHour;
        this.locationFilter.endDt = nextHour;
    }

    private async buildDateModel(): Promise<void> {
        const eventFormModelUtil = (await AngBridgeUtil.$inject("EventFormModelUtil")) as EventFormModelUtilFactory;
        if (!this.isAlive()) {
            return;
        }

        this.dateModel = eventFormModelUtil.decorateModelMethods({
            suppressedDatetimeFields: ["setup", "pre", "post", "takedown", "allDay"],
            hasSpansMidnight: false,
            express: true,
            profileCode: EXPRESS_PROFILE_CODE,
            mode: "create",
            timeModel: {
                evStartDt: null,
                evEndDt: null,
                minutes: {},
            },
            locations: [],
            removedLocations: [],
            addedLocations: [],
            resources: [],
            removedResources: [],
            addedResources: [],
            onInitDateTimeChange: this.onDateTimeChange,
            occUUID: 0,
            objectOccMap: {},
            occurrences: [],
            itemDict: {},
            items: [],
            availabilityPromises: [],
            quotaPromises: [],
            dirtySearch: {},
            searchCounts: {},
            clickToPickApiBean: { totalCalendarRefresh: noop },
        });

        const now = new Date();
        const minCreate = S25Util.date.addDays(now, -365 * EXPRESS_MIN_CREATE_YEARS_PAST);
        minCreate.setMilliseconds(0);
        minCreate.setMinutes(0);
        minCreate.setHours(0);
        this.dateModel.minCreateDate = minCreate;
        this.dateModel.maxCreateDate = S25Util.date.addDays(now, 365 * EXPRESS_MAX_CREATE_YEARS_FUTURE);
    }

    private async applyInitContext(): Promise<void> {
        const initContext = this.initContext;
        if (!initContext || !this.dateModel) {
            return;
        }

        let location: ExpressSchedulingLocationItem | null = null;
        if (initContext.itemId) {
            const spaces = await SpaceService.getSpacesIncludes([initContext.itemId], ["layouts", "direct_schedule"]);
            if (!this.isAlive()) {
                return;
            }
            const space = spaces?.length ? spaces[0] : undefined;
            if (space) {
                location = {
                    itemId: space.space_id,
                    itemName: space.space_name,
                    itemTypeId: Item.Ids.Location,
                    duration:
                        space.direct_schedule && parseInt(String(space.direct_schedule.direct_schedule_duration), 10),
                } as ExpressSchedulingLocationItem;
            }
        }

        if (initContext.startDt && initContext.endDt) {
            if (
                location &&
                !this.validateDuration(initContext.startDt, initContext.endDt, location) &&
                (location.duration ?? 0) > 0
            ) {
                initContext.endDt = S25Util.date.addMinutes(initContext.startDt, location.duration!);
            }
            this.location = location;
            this.touchedDateTimes = true;
            this.dateModel.timeModel.evStartDt = initContext.startDt;
            this.dateModel.timeModel.evEndDt = initContext.endDt;
            this.locationFilter.startDt = initContext.startDt;
            this.locationFilter.endDt = initContext.endDt;
            this.dateModel.addOccurrence(initContext.startDt, initContext.endDt, {}, false, EXPRESS_PROFILE_CODE);
            if (location) {
                await this.onSelectLocation();
            }
        } else {
            this.location = location;
            if (location) {
                await this.onSelectLocation();
            }
        }
    }

    private filterAvailableExpressLocations(
        items: ExpressSchedulingLocationItem[],
    ): Promise<ExpressSchedulingLocationItem[]> {
        if (!this.dateModel) {
            return Promise.resolve(items);
        }
        const copiedItems = S25Util.deepCopy(items);
        for (const item of copiedItems) {
            this.dateModel.removeObject(item);
        }
        for (const item of copiedItems) {
            this.locationPromises.push(this.dateModel.addObject(item, true));
        }

        return this.promiseWait().then(() => {
            if (!this.isAlive()) {
                return copiedItems;
            }
            const dateModel = this.dateModel!;
            const availabilityPromise = dateModel.checkAvailability(dateModel, dateModel.locations).then(() => {
                if (!this.isAlive()) {
                    return copiedItems;
                }
                const availItems = dateModel.locations.filter((item) => !item.checkedObj?.hasConflicts);
                const available = copiedItems.filter(
                    (item) => S25Util.array.findByProp(availItems, "itemId", item.itemId) > -1,
                );
                for (const item of copiedItems) {
                    dateModel.removeObject(item);
                }
                return available;
            });
            this.availabilityPromises.push(availabilityPromise);
            return availabilityPromise;
        });
    }

    private validateDuration(startDt: Date, endDt: Date, location: ExpressSchedulingLocationItem): boolean {
        location.duration =
            location.duration ||
            (location.direct_schedule && parseInt(String(location.direct_schedule.direct_schedule_duration), 10));
        if (location.duration && startDt && endDt) {
            if (location.duration < S25Util.date.diffMinutes(startDt, endDt)) {
                return false;
            }
        }
        return true;
    }

    private setDurationWarning(): Promise<void> {
        return this.promiseWait().then(() =>
            delay(EXPRESS_FORM_SETUP_DELAY_MS).then(() => {
                if (!this.isAlive()) {
                    return;
                }
                const timeRange = this.getTimeRange();
                if (!timeRange) {
                    return;
                }
                this.durationWarning = "";
                const eventDuration = S25Util.date.diffMinutes(timeRange.startDt, timeRange.endDt);
                if (this.location && !this.validateDuration(timeRange.startDt, timeRange.endDt, this.location)) {
                    this.durationWarning = DURATION_WARNING.replace(
                        "{{LOCATION_MINUTES}}",
                        String(this.location.duration),
                    ).replace("{{EVENT_MINUTES}}", String(eventDuration));
                }
                this.refreshCanCreateExpress();
                this.notifyStateChange();
            }),
        );
    }

    private setQuotaWarning(): Promise<void> {
        this.quotaWarning = "";
        return this.promiseWait().then(() =>
            delay(EXPRESS_FORM_SETUP_DELAY_MS).then(() => {
                if (!this.isAlive() || !this.dateModel) {
                    return;
                }
                if (!this.location) {
                    this.clearOccurrenceQuotaState(this.dateModel);
                    this.notifyStateChange();
                    return;
                }
                const conflictOccurrence = this.dateModel.occurrences.find((occurrence) => occurrence.hasQuotaConflict);
                const conflict = conflictOccurrence?.quotaConflict;
                if (conflict) {
                    this.quotaWarning = this.dateModel.getQuotaMsg(this.enrichQuotaConflict(conflict));
                }
                this.notifyStateChange();
            }),
        );
    }

    private promiseWait(): Promise<void> {
        return delay(EXPRESS_PROMISE_YIELD_MS)
            .then((): Promise<unknown[]> => Promise.all(this.locationPromises))
            .then(() => delay(EXPRESS_PROMISE_YIELD_MS))
            .then((): Promise<unknown[]> => Promise.all(this.quotaPromises))
            .then(() => delay(EXPRESS_PROMISE_YIELD_MS))
            .then((): Promise<unknown[]> => Promise.all(this.availabilityPromises))
            .then(() => delay(EXPRESS_PROMISE_YIELD_MS))
            .then((): void => undefined);
    }

    private syncExpressEventTypeOnModel(location: ExpressSchedulingLocationItem | null | undefined): void {
        const dateModel = this.dateModel;
        if (!dateModel) {
            return;
        }
        const eventTypeId = location?.eventTypeId;
        if (!eventTypeId) {
            if (dateModel.itemDict?.type) {
                delete dateModel.itemDict.type;
            }
            return;
        }
        dateModel.itemDict ??= {};
        dateModel.itemDict.type = {
            data: {
                itemId: eventTypeId,
            },
        };
    }

    private getTimeRange(): ExpressSchedulingTimeRange | undefined {
        const startDt = this.dateModel?.timeModel.evStartDt;
        const endDt = this.dateModel?.timeModel.evEndDt;
        if (!startDt || !endDt) {
            return undefined;
        }
        return { startDt, endDt };
    }

    private getSaveLocation(location: ExpressSchedulingLocationItem): ExpressSchedulingSaveLocation | undefined {
        const itemId = location.itemId;
        const eventTypeId = location.eventTypeId;
        if (itemId === undefined || eventTypeId === undefined) {
            return undefined;
        }
        return {
            ...location,
            itemId,
            eventTypeId,
            scheduler: location.scheduler,
        };
    }

    private async ensureSelectedLocationDetails(): Promise<void> {
        const dateModel = this.dateModel;
        const location = this.location;
        if (!dateModel || !location || this.getSaveLocation(location)) {
            return;
        }
        this.normalizeLocationItemType(location);
        this.locationPromises.push(dateModel.setObjectDetails([location]));
        await this.promiseWait();
    }

    private normalizeLocationItemType(location: ExpressSchedulingLocationItem): void {
        location.itemTypeId ??= Item.Ids.Location;
    }

    private postExpressEvent(
        saveLocation: ExpressSchedulingSaveLocation,
        start: string,
        end: string,
        selectedParentId?: ExpressSchedulingSelectedParentId,
    ): Promise<unknown> {
        return EventService.postExpressEvent(
            this.name,
            saveLocation.itemId,
            saveLocation.eventTypeId,
            saveLocation.scheduler?.itemId ?? 0,
            EXPRESS_SAVE_EVENT_STATE,
            start,
            end,
            true,
            toRoseExpressParentId(selectedParentId),
        );
    }

    private clearDateModelLocations(): void {
        const dateModel = this.dateModel;
        if (!dateModel) {
            return;
        }
        for (const item of [...dateModel.locations]) {
            dateModel.removeObject(item);
        }
    }

    private clearOccurrenceQuotaState(dateModel: ExpressSchedulingDateModel): void {
        S25Util.propertyDelete(dateModel.occurrences, "hasQuotaConflict");
        S25Util.propertyDelete(dateModel.occurrences, "quotaConflict");
    }

    private resetLocationDropdownApi(): void {
        for (const key of Object.keys(this.locationDropdownApi)) {
            delete this.locationDropdownApi[key];
        }
    }

    private enrichQuotaConflict(conflict: ExpressSchedulingQuotaConflict): ExpressSchedulingQuotaConflict {
        conflict.location = this.location;
        return conflict;
    }

    private showValidationError(message: string): void {
        void AlertService.alert(message);
    }

    private isAlive(): boolean {
        return !this.destroyed;
    }

    private notifyStateChange(): void {
        if (!this.destroyed) {
            this.onStateChange();
        }
    }
}
